import { NavigateFunction } from 'react-router-dom';
import { Dispatch } from 'redux';
import { loginApiCall } from '../../services/api/users';
import {
  normalizeMfaChallenge,
  type MfaRequiredPayload,
  type MfaSetupRequiredPayload,
} from '../../types/authMfa';
import { SET_USER } from '../../utils/constants/action-types';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { getSafeRedirectPath } from '../../utils/redirectAfterLogin';
import { decodeJwtToken, returnUserFromLocalStorage } from '../../utils/helpers/validateuser';
import { setToken } from '../../utils/security/tokenStorage';

interface Credentials {
  email: string;
  password: string;
}

interface ErrorResponse {
  response: {
    data: {
      message: string;
    };
  };
}

async function applyAccessTokenAndNavigate(
  accessToken: string,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  redirectPath: string | undefined,
  onError: (message: string) => void
): Promise<boolean> {
  try {
    setToken(accessToken);
  } catch (tokenError) {
    console.error('[Login] Failed to store token securely:', tokenError);
    onError('Failed to store authentication token. Please try again.');
    return false;
  }

  const userData = decodeJwtToken(accessToken);
  dispatch({ type: SET_USER, payload: userData });
  const safePath = getSafeRedirectPath(redirectPath, APP_ROUTES.HOME);
  navigate(safePath);
  return true;
}

/** Store token, hydrate user, and navigate (e.g. after MFA verify). */
export async function completeLoginWithAccessToken(
  accessToken: string,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  onError: (message: string) => void,
  redirectPath?: string
): Promise<void> {
  await applyAccessTokenAndNavigate(accessToken, dispatch, navigate, redirectPath, onError);
}

export const setUserAfterLogin = () => {
  return async (dispatch: Dispatch) => {
    const user = returnUserFromLocalStorage();
    if (user) {
      dispatch({ type: SET_USER, payload: user });
    }
  };
};

/** Store JWT and refresh Redux user without navigation (e.g. after MFA step-up verify). */
export function applySessionToken(accessToken: string, dispatch: Dispatch): void {
  setToken(accessToken);
  dispatch({ type: SET_USER, payload: decodeJwtToken(accessToken) });
}

export const loginAction = (
  credentials: Credentials,
  setLoading: (loading: boolean) => void,
  onError: (message: string) => void,
  navigate: NavigateFunction,
  redirectPath?: string,
  onMfaRequired?: (payload: MfaRequiredPayload) => void,
  onMfaSetupRequired?: (payload: MfaSetupRequiredPayload) => void
) => {
  return async (dispatch: Dispatch) => {
    try {
      const { data } = await loginApiCall(credentials);

      if ('mfaRequired' in data && data.mfaRequired) {
        onMfaRequired?.({
          mfaContextToken: data.mfaContextToken,
          mfaChallenge: normalizeMfaChallenge(data.mfaChallenge),
        });
        setLoading(false);
        return;
      }

      if ('mfaSetupRequired' in data && data.mfaSetupRequired && data.accessToken) {
        onMfaSetupRequired?.({ accessToken: data.accessToken });
        setLoading(false);
        return;
      }

      if ('accessToken' in data && data.accessToken) {
        const ok = await applyAccessTokenAndNavigate(
          data.accessToken,
          dispatch,
          navigate,
          redirectPath,
          onError
        );
        if (!ok) {
          setLoading(false);
        }
        return;
      }

      onError('Unexpected login response. Please try again.');
    } catch (error) {
      const err = error as ErrorResponse;
      if (err.response?.data?.message) {
        onError(err.response.data.message);
      } else {
        onError('Something went wrong, please try again later.');
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
};
